import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { OutletProvider } from "./context/OutletContext";import Layout, { ReferralTracker } from "./components/Layout";
import Home from "./pages/Home";
import Products, { CategoriesPage } from "./pages/Products";
import CategoryPage from "./pages/CategoryPage";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import SakhiLanding from "./pages/SakhiLanding";
import Affiliate from "./pages/Affiliate";
import Login from "./pages/Login";
import StyleHub from "./pages/StyleHub";
import BusinessHub from "./pages/BusinessHub";
import PartnerHub from "./pages/PartnerHub";
import ControlRoom from "./pages/ControlRoom";
import RiderHub from "./pages/RiderHub";
import RoleLogin from "./pages/RoleLogin";
import Signup from "./pages/Signup";
import Splash from "./components/Splash";
import NotFound from "./pages/NotFound";
import MetaPixel from "./analytics/MetaPixel";
import Club from "./pages/Club";
import WalletPage from "./pages/Wallet";
import Drops from "./pages/Drops";
import SecretLab from "./pages/SecretLab";
import Community from "./pages/Community";
import Creators from "./pages/Creators";
import CreatorDashboard from "./pages/CreatorDashboard";
import ClubRefer from "./pages/ClubRefer";
import Discover from "./pages/Discover";
import PostLook from "./pages/PostLook";
import SakhiWall from "./pages/SakhiWall";
import AskSakhis from "./pages/AskSakhis";
import AskQuestion from "./pages/AskQuestion";
import SakhiChat from "./pages/SakhiChat";
import SakhiProfile from "./pages/SakhiProfile";
import Challenge from "./pages/Challenge";
import Trust from "./pages/Trust";
import { About, Contact, AffiliateTerms } from "./pages/Static";

function CreatorRedirect() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  useEffect(() => {
    if (ref) {
      localStorage.setItem("suvikka_creator_ref", ref);
      import("./lib/api").then(({ API }) => API.post(`/creators/track/${ref}`).catch(() => {}));
    }
  }, [ref]);
  return <Navigate to={`/product/${slug}`} replace />;
}

const PRIVATE_PREFIXES = ["/style-hub", "/business-hub", "/partner-hub", "/control-room",
  "/growth-desk", "/rider", "/cart", "/checkout", "/login", "/signup", "/register",
  "/outlet-partner/login", "/rider/login", "/admin/login"];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function RouteSeo() {
  const loc = useLocation();
  const priv = PRIVATE_PREFIXES.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  let el = document.head.querySelector('meta[name="robots"]');
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
  el.setAttribute("content", priv ? "noindex, nofollow" : "index, follow");
  return null;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-stone-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/trending" element={<Products key="trending" mode="trending" />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Protected><Cart /></Protected>} />
        <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
        <Route path="/open-outlet" element={<Navigate to="/suvikka-sakhi" replace />} />
        <Route path="/outlet-enquiry" element={<Navigate to="/suvikka-sakhi" replace />} />
        <Route path="/affiliate" element={<Affiliate />} />
        <Route path="/suvikka-sakhi" element={<SakhiLanding />} />
        <Route path="/club" element={<Club />} />
        <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
        <Route path="/drops" element={<Drops />} />
        <Route path="/secret-lab" element={<SecretLab />} />
        <Route path="/secret-lab/:slug" element={<SecretLab />} />
        <Route path="/community" element={<Community />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/share-look" element={<PostLook />} />
        <Route path="/sakhi-wall" element={<SakhiWall />} />
        <Route path="/ask-sakhis" element={<AskSakhis />} />
        <Route path="/ask-sakhis/:id" element={<AskQuestion />} />
        <Route path="/sakhi-chat" element={<SakhiChat />} />
        <Route path="/community/share" element={<Community />} />
        <Route path="/sakhi/:username" element={<SakhiProfile />} />
        <Route path="/challenge/:id" element={<Challenge />} />
        <Route path="/creators" element={<Creators />} />
        <Route path="/creator/dashboard" element={<Protected><CreatorDashboard /></Protected>} />
        <Route path="/club/refer" element={<Protected><ClubRefer /></Protected>} />
        <Route path="/trust" element={<Trust />} />
        <Route path="/p/:slug" element={<CreatorRedirect />} />
        <Route path="/affiliate-terms" element={<AffiliateTerms />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Signup />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/outlet-partner/login" element={<RoleLogin kind="outlet" />} />
        <Route path="/rider/login" element={<RoleLogin kind="rider" />} />
        <Route path="/admin/login" element={<RoleLogin kind="admin" />} />
        <Route path="/style-hub" element={<Protected roles={["customer"]}><StyleHub /></Protected>} />
        <Route path="/business-hub" element={<Protected roles={["retail_owner"]}><BusinessHub /></Protected>} />
        <Route path="/partner-hub" element={<Protected roles={["affiliate"]}><PartnerHub /></Protected>} />
        <Route path="/control-room" element={<Protected roles={["admin"]}><ControlRoom /></Protected>} />
        <Route path="/growth-desk" element={<Protected roles={["staff", "admin"]}><ControlRoom /></Protected>} />
        <Route path="/rider" element={<Protected roles={["rider"]}><RiderHub /></Protected>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <OutletProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ReferralTracker />
            <RouteSeo />
            <MetaPixel />
            <Toaster position="top-center" richColors />
            <Splash />
            <AppRoutes />
          </BrowserRouter>
        </OutletProvider>
      </CartProvider>
    </AuthProvider>
  );
}
